import React from 'react'
import styles from '../Viewer.module.scss'
import {getGradient, getGradientWidth, shadowVariable} from '../utils'
import { Tooltip } from 'antd'
import CellTooltip from '../Tooltips/CellTooltip'
import {useActionsColors, useCommonTheme, useCurrentTheme} from '../../hooks/useCurrentTheme'
import {useMatrixFilterColor} from '../../hooks/useMatrixFilterColor'
import {setLineSPFData, setSelectedHandsOnePlayer, setSelectedHandsTwoPlayers} from '../../Redux/_actions/viewer'
import MultiMatrixCellText from './MultiMatrixCellText'
import {useDispatch, useSelector} from 'react-redux'
import {createSelector} from '@reduxjs/toolkit'
import {useTypeSelector} from "../../hooks/useTypeHooks";
import {selectDataNew} from "../../Redux/selectors/selectors";

const selectMemoView = createSelector(
    [
        (state) => state.preferences.dataNew.common.matrix.threshold,
        (state) => state.view.activeTwoPlayersFiltersTab,
        (state) => state.view.lineSPFData,
        (state) => state.preferences.dataNew.common.actionsOrder,
        (state) => state.preferences.dataNew.common.isDrawReverseActions,
        (state) => state.preferences.dataNew.common.viewer.matrixTooltip,
    ],
    (threshold, activeTwoPlayersFiltersTab, lineSPFData, actionsOrder, isDrawReverseRaiseBetActions, isMatrixTooltip) => {
        return {
            threshold,
            activeTwoPlayersFiltersTab,
            lineSPFData,
            actionsOrder,
            isDrawReverseRaiseBetActions,
            isMatrixTooltip,
        }
    }
)

function MultiMatrixCell({
                             el,
                             weight,
                             viewMode,
                             isTrainer,
                             moveMode,
                             isNormalize,
                             selectedCell,
                             selectedCellsFunc,
                             setSelectedCellMode,
                             isSmallTrainerMatrix,
                             isSettings,
                             eqrMax,
                         }) {
    const {threshold, activeTwoPlayersFiltersTab, lineSPFData, isDrawReverseRaiseBetActions, isMatrixTooltip} =
        useSelector(selectMemoView)
    const {common} = useTypeSelector(selectDataNew)
    const dispatch = useDispatch()
    const getColor = useActionsColors()
    const getFillColor = useMatrixFilterColor()
    const theme = useCurrentTheme()
    const {color, textcolor2playermatrix, textcolor1playermatrix: fontColorOnePlayer} = theme.matrix.substrate
    const customBorderColor = theme.matrix.substrate['border color']
    const {matrix} = useCommonTheme()
    const {useSubstrate, fontSize2player, rounding, shadow, fontSize1player: fontSizeOnePlayer} = matrix
    const roundStyle = rounding ? styles.rounding : styles.withoutRounding
    const textcolor1playermatrix = fontColorOnePlayer
    const textShadow = shadowVariable[shadow]
    const fontSize1player = fontSizeOnePlayer
    const {selectedMatrixIndex: selectedMatrixIndexSPFLine, sortedEquities: sortedEquitiesSPFLine} = lineSPFData

    const {isShowDetailedTooltip} = common

    const fillColor = getFillColor({
        field: el.field,
        played: el.played,
        fieldMax: el.matrixFields?.fieldMatrixMax,
        fieldMin: el.matrixFields?.fieldMatrixMin,
        matrixRange: theme.matrix.range,
        matrixEquity: theme.matrix.equity,
        matrixEv: theme.matrix.ev,
        matrixEqr: theme.matrix.eqr,
        minEquityRealization: 0,
        maxEquityRealization: eqrMax,
    })

    const onClickCell = (el) => {
        const index = el.cellIndex

        // left (action) button click
        setSelectedCellMode(true)

        // single cell selection
        // unselect single cell
        if (selectedCell.length === 1 && selectedCell.includes(index) && !moveMode) {
            selectedCell = [index]
        } else if (selectedCell.length === 1 && selectedCell.includes(index) && moveMode) {
            selectedCell = [index]
            setSelectedCellMode(false)
        } else {
            selectedCell = [index]
        }

        selectedCellsFunc(selectedCell)
    }

    const onMoveCell = (el) => {
        const index = el.cellIndex

        if (!moveMode) {
            selectedCellsFunc([index])
        }
    }

    const highlightLineSPFClusters = (event, el) => {
        const index = el.cellIndex

        if (selectedMatrixIndexSPFLine.includes(index) && selectedMatrixIndexSPFLine.length === 1) {
            const lineSpfData = {
                selectedEquity: [],
                selectedMatrixIndex: [],
                selectedClusterIndex: [],
                isHover: false,
            }

            dispatch(setLineSPFData(lineSpfData))
        } else {
            let matrixIndexes = [index]
            let equities = new Set()

            el.vals[0].cards.c.forEach((card) => {
                const equityCard = card.eq.toFixed(2)

                equities.add(equityCard)
            })

            equities = Array.from(equities)

            let newSelectedClusters = equities.map((equity) => {
                if (sortedEquitiesSPFLine.indexOf(equity) > -1) {
                    return sortedEquitiesSPFLine.indexOf(equity)
                }
            })

            dispatch(
                setLineSPFData({
                    selectedMatrixIndex: matrixIndexes,
                    selectedEquity: equities,
                    selectedClusterIndex: newSelectedClusters,
                    isHover: false,
                })
            )
        }
    }

    const handleMouseLeave = () => {
        if (!moveMode) {
            if (viewMode === 'onePlayer') {
                dispatch(setSelectedHandsOnePlayer([]))
            } else {
                dispatch(setSelectedHandsTwoPlayers([]))
            }
        }
    }

    // const revertedValues = el.vals
    const revertedValues = el.isStrategy && isDrawReverseRaiseBetActions ? el.vals.slice().reverse() : el.vals

    const gradient = el.isStrategy
        ? getGradient(revertedValues, el.filter, getColor, threshold)
        : `linear-gradient(to right, ${fillColor} , ${fillColor})`

    const isShowTooltip = el.isStrategy && el.isShowCellTooltip

    const tdStyles = !el.isHidden && {
        '--custom-border-color': `${customBorderColor}`,
        borderRadius: 'none',
        transition: isTrainer && isSmallTrainerMatrix && 'all .2s',

        textShadow,
        border:
            isTrainer &&
            isSmallTrainerMatrix &&
            (el.trainerCardIndex !== el.cellIndex
                ? '0.5px solid #000'
                : isTrainer && el.trainerCardIndex === el.cellIndex && ''),
        outline:
            selectedCell.includes(el.cellIndex) || el.trainerCardIndex === el.cellIndex
                ? '1px solid var(--custom-border-color)'
                : '',
        outlineOffset: '-1px',
        backgroundImage: gradient,
        backgroundSize: getGradientWidth(
            revertedValues,
            el.filter,
            threshold,
            weight,
            el.matrixFields?.absMatrixMax,
            el.isStrategy
        ),
        zIndex:
            selectedCell.includes(el.cellIndex) || (isSmallTrainerMatrix && el.trainerCardIndex === el.cellIndex) ? 1 : 0,
        cursor: isShowTooltip ? 'pointer' : 'default',
    }

    const styleClasses = [
        styles.cell,
        roundStyle,
        el.isHidden,
        styles.gradientBackground,
        activeTwoPlayersFiltersTab && activeTwoPlayersFiltersTab.includes('EQBUCKETS') && viewMode === 'twoPlayers'
            ? el.isEqBucketOpacity
            : el.isOpacity,
    ].join(' ')

    return (
        <>
            {!el.isStrategy && !el.isHidden ? (
                <td
                    style={tdStyles}
                    className={styleClasses}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => !isSettings && onClickCell(el)}
                    onMouseEnter={() => !moveMode && !isSettings && onMoveCell(el)}
                >
                    {/* {el.played !== 0 && ( */}
                    <MultiMatrixCellText
                        isSmallTrainerMatrix={isSmallTrainerMatrix}
                        rounding={rounding}
                        isStrategy={el.isStrategy}
                        isOnePlayer={el.isOnePlayer}
                        useSubstrate={useSubstrate}
                        fontSize1player={fontSize1player}
                        fontSize2player={fontSize2player}
                        isNormalize={isNormalize}
                        textcolor1playermatrix={textcolor1playermatrix}
                        textcolor2playermatrix={textcolor2playermatrix}
                        backgroundColor={color}
                        field={el.field}
                        text={el.text}
                        played={el.played}
                        isTrainer={isTrainer}
                    />
                    {/* )} */}
                </td>
            ) : el.isStrategy && !el.isHidden ? (
                <Tooltip
                    title={
                        isMatrixTooltip &&
                        isShowTooltip &&
                        !isSettings && (
                            <CellTooltip
                                values={el.vals}
                                isStrategy={el.isStrategy}
                                cardString={el.cardString}
                                filter={el.filter}
                                isTrainer={isTrainer}
                                isMatrix
                                useAllCards={true}
                            />
                        )
                    }
                    arrow={false}
                    // overlayClassName={
                    //     !isShowDetailedTooltip ? styles.cardTooltip : null
                    //
                    // }
                    overlayInnerStyle={{width: isShowDetailedTooltip ? 300 + el.vals.length * 60 : 320}}
                    placement='bottomRight'
                    mouseEnterDelay={0.4}
                    mouseLeaveDelay={0.0}
                >
                    <td
                        style={tdStyles}
                        className={styleClasses}
                        onMouseLeave={handleMouseLeave}
                        onMouseEnter={() => !moveMode && !isTrainer && !isSettings && onMoveCell(el)}
                        onClick={(e) => {
                            !isTrainer && !isSettings && onClickCell(el, e)
                            !isSettings && highlightLineSPFClusters(e, el)
                        }}
                    >
                        <MultiMatrixCellText
                            isSmallTrainerMatrix={isSmallTrainerMatrix}
                            rounding={rounding}
                            isStrategy={el.isStrategy}
                            isOnePlayer={el.isOnePlayer}
                            useSubstrate={useSubstrate}
                            fontSize1player={fontSize1player}
                            fontSize2player={fontSize2player}
                            isNormalize={isNormalize}
                            textcolor1playermatrix={textcolor1playermatrix}
                            textcolor2playermatrix={textcolor2playermatrix}
                            backgroundColor={color}
                            field={el.field}
                            text={el.text}
                            played={el.played}
                            addedData={el.addedData}
                            isTrainer={isTrainer}
                        />
                    </td>
								</Tooltip>
            ) : (
                <td className={[styles.emptyCell, el.hidden, roundStyle].join(' ')}/>
            )}
        </>
    )
}

export default React.memo(MultiMatrixCell, (prevProps, nextProps) => {
    return (
        prevProps.weight === nextProps.weight &&
        prevProps.el === nextProps.el &&
        prevProps.el.played === nextProps.el.played &&
        prevProps.viewMode === nextProps.viewMode &&
        prevProps.isTrainer === nextProps.isTrainer &&
        prevProps.isNormalize === nextProps.isNormalize &&
        prevProps.selectedCellsFunc === nextProps.selectedCellsFunc &&
        prevProps.setSelectedCellMode === nextProps.setSelectedCellMode &&
        !nextProps.selectedCell.includes(nextProps.el.cellIndex) &&
        !prevProps.selectedCell.includes(prevProps.el.cellIndex) &&
        !nextProps.moveMode &&
        !prevProps.moveMode
    )
})
